import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import { Icon } from './Icon';
import {
  WELCOME_TOUR_EVENT,
  finishWelcomeTour,
  hasFinishedWelcomeTour,
  welcomeTourStepsForRole,
  type WelcomeTourRole,
} from '../lib/welcomeTour';
import './WelcomeTour.css';

interface WelcomeTourProps {
  viewerKey: string | null;
  role: WelcomeTourRole | null;
  ready?: boolean;
}

export default function WelcomeTour({ viewerKey, role, ready = true }: WelcomeTourProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const steps = useMemo(() => (role ? welcomeTourStepsForRole(role) : []), [role]);

  useEffect(() => {
    setOpen(false);
    setStepIndex(0);
    if (!ready || !viewerKey || !role || hasFinishedWelcomeTour(viewerKey)) return;
    const timer = window.setTimeout(() => setOpen(true), 500);
    return () => window.clearTimeout(timer);
  }, [ready, role, viewerKey]);

  useEffect(() => {
    const reopen = () => {
      if (!ready || !viewerKey || !role) return;
      setStepIndex(0);
      setOpen(true);
    };
    window.addEventListener(WELCOME_TOUR_EVENT, reopen);
    return () => window.removeEventListener(WELCOME_TOUR_EVENT, reopen);
  }, [ready, role, viewerKey]);

  useEffect(() => {
    if (!open) return;
    cardRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (viewerKey) finishWelcomeTour(viewerKey);
      setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open, viewerKey]);

  if (!open || !viewerKey || !role || steps.length === 0) return null;
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const lastStep = stepIndex === steps.length - 1;
  const onFinish = () => {
    finishWelcomeTour(viewerKey);
    setOpen(false);
  };

  return (
    <div className="welcome-tour-overlay no-print" role="presentation">
      <div
        ref={cardRef}
        className="welcome-tour-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-tour-title"
        tabIndex={-1}
      >
        <div className="welcome-tour-topline">
          <span className="welcome-tour-kicker">Quick Hub tour</span>
          <button type="button" className="welcome-tour-skip" onClick={onFinish}>
            Skip
          </button>
        </div>

        <div className="welcome-tour-icon" aria-hidden="true">
          <Icon name={step.id === 'music' ? 'music' : step.id === 'messages' ? 'chat-bubble' : step.id === 'calendar' ? 'calendar' : step.id === 'polls' || step.id === 'todos' ? 'clipboard' : step.id === 'command-center' ? 'search' : 'wave-hello'} size={28} />
        </div>
        <h2 id="welcome-tour-title" className="welcome-tour-title">{step.title}</h2>
        <p className="welcome-tour-body">{step.body}</p>

        {step.href && (
          <button
            type="button"
            className="welcome-tour-show"
            onClick={() => navigate(step.href!)}
          >
            <Icon name="search" size={17} />
            {location.pathname === step.href ? 'You are on this page' : step.actionLabel ?? 'Open this page'}
          </button>
        )}

        <div className="welcome-tour-progress" aria-label={`Step ${stepIndex + 1} of ${steps.length}`}>
          {steps.map((tourStep, index) => (
            <span
              key={tourStep.id}
              className={index === stepIndex ? 'welcome-tour-dot welcome-tour-dot-active' : 'welcome-tour-dot'}
            />
          ))}
        </div>

        <div className="welcome-tour-actions">
          {stepIndex > 0 && (
            <Button type="button" variant="secondary" onClick={() => setStepIndex((index) => index - 1)}>
              Back
            </Button>
          )}
          <Button
            type="button"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              if (lastStep) onFinish();
              else setStepIndex((index) => index + 1);
            }}
          >
            {lastStep ? 'Finish tour' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}
